import { useState, useEffect } from 'react';
import { getSpecies, getBreedsBySpecies, Species, Breed } from '../services/categoryService';

interface UseSpeciesAndBreedsReturn {
  species: Species[];
  breeds: Breed[];
  isLoadingSpecies: boolean;
  isLoadingBreeds: boolean;
  selectedSpeciesId: number | null;
  setSelectedSpeciesId: (speciesId: number | null) => void;
  speciesError: string | null;
  breedsError: string | null;
}

export function useSpeciesAndBreeds(): UseSpeciesAndBreedsReturn {
  const [species, setSpecies] = useState<Species[]>([]);
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [isLoadingSpecies, setIsLoadingSpecies] = useState(true);
  const [isLoadingBreeds, setIsLoadingBreeds] = useState(false);
  const [selectedSpeciesId, setSelectedSpeciesId] = useState<number | null>(null);
  const [speciesError, setSpeciesError] = useState<string | null>(null);
  const [breedsError, setBreedsError] = useState<string | null>(null);

  // Load species on mount
  useEffect(() => {
    const loadSpecies = async () => {
      try {
        setIsLoadingSpecies(true);
        setSpeciesError(null);
        const speciesData = await getSpecies();
        setSpecies(speciesData);
      } catch (error: any) {
        console.error('Error loading species:', error);
        setSpeciesError(error.message || 'Failed to load species');
      } finally {
        setIsLoadingSpecies(false);
      }
    };

    loadSpecies();
  }, []);

  // Load breeds when species changes
  useEffect(() => {
    const loadBreeds = async () => {
      if (!selectedSpeciesId) {
        setBreeds([]);
        return;
      }

      try {
        setIsLoadingBreeds(true);
        setBreedsError(null);
        const breedsData = await getBreedsBySpecies(selectedSpeciesId);
        setBreeds(breedsData);
      } catch (error: any) {
        console.error('Error loading breeds:', error);
        setBreedsError(error.message || 'Failed to load breeds');
        setBreeds([]);
      } finally {
        setIsLoadingBreeds(false);
      }
    };

    loadBreeds();
  }, [selectedSpeciesId]);

  return {
    species,
    breeds,
    isLoadingSpecies,
    isLoadingBreeds,
    selectedSpeciesId,
    setSelectedSpeciesId,
    speciesError,
    breedsError,
  };
}